<script>
import AkLabel from '@components/v2/general/AkLabel';
import Calendar from 'primevue/calendar';
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';

export default {
  components: {AkLabel, Calendar, AkDialogHeadless},
  inject: {
    pSubmitted: {default: null},
    pDisabled: {default: null},
    pIgnoredValidator: {default: null},
  },
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    showTime: {
      type: Boolean,
      required: false,
      default: false,
    },
    timeOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    showButtonBar: {
      type: Boolean,
      required: false,
      default: true,
    },
    label: {
      type: String,
      required: false,
      default: undefined,
    },
    modelValue: {
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    inputClass: {
      type: String,
      required: false,
      default: '',
    },
    customValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    format: {
      type: String,
      required: false,
      default: 'dd/mm/yy',
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    maxDate: {
      type: Date,
      required: false,
      default: undefined,
    },
    baseZIndex: {
      type: Number,
      required: false,
      default: 0,
    },
    minDate: {
      type: Date,
      required: false,
      default: undefined,
    },
    selection: {
      type: String,
      required: false,
      default: 'single',
    },
    isForm: {
      type: Boolean,
      required: false,
      default: true,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    view: {
      type: String,
      required: false,
      default: 'date',
    },
    hideOnRangeSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    icon: {
      type: String,
      required: false,
      default: null,
    },
    disabledDates: {
      type: Array,
      required: false,
      default: undefined,
    },
    hideRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue', 'value-change'],
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    isInline() {
      return this.inline;
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        for (let error of this.validator.$errors) {
          if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
            return true;
          }
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isValidatorMinDate() {
      return (
        this.validator !== undefined &&
        this.validator.minDate !== undefined &&
        this.validator.minDate.$invalid &&
        this.alreadySubmitted
      );
    },
    isValidatorMaxDate() {
      return (
        this.validator !== undefined &&
        this.validator.maxDate !== undefined &&
        this.validator.maxDate.$invalid &&
        this.alreadySubmitted
      );
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },
    isRequiredFailed() {
      return (
        this.isRequired &&
        this.getIgnoredValidator.indexOf('required') === -1 &&
        this.validator.required.$invalid &&
        this.alreadySubmitted
      );
    },
    isCustomValidatorFailed() {
      if (this.customValidator)
        for (let v of this.customValidator) {
          let error =
            v !== undefined &&
            this.validator !== undefined &&
            this.validator[v] !== undefined &&
            this.validator[v].$invalid &&
            this.alreadySubmitted;
          if (error) return true;
        }
      return false;
    },
    getCustomValidatorMessage() {
      if (this.customValidator)
        for (let v of this.customValidator) {
          let error =
            v !== undefined &&
            this.validator !== undefined &&
            this.validator[v] !== undefined &&
            this.validator[v].$invalid &&
            this.alreadySubmitted;
          if (error) return this.validator[v].$message;
        }
      return undefined;
    },
    formattedValue() {
      if (!this.currentValue) return '';

      const toDate = (v) => (v instanceof Date ? v : new Date(v));

      // format date basic fallback for displaying
      if (this.currentValue instanceof Date) {
        return this.currentValue.toLocaleDateString();
      }

      if (Array.isArray(this.currentValue)) {
        const start = this.currentValue[0] ? toDate(this.currentValue[0]).toLocaleDateString() : '';
        const end = this.currentValue[1] ? toDate(this.currentValue[1]).toLocaleDateString() : '';
        if (start && end) return `${start} - ${end}`;
        if (start) return start;
      }
      return this.currentValue;
    },
  },
  methods: {
    onHide(event) {
      if (this.selection === 'range') {
        if (!this.currentValue || this.currentValue.length !== 2 || this.currentValue[1] == undefined) {
          this.currentValue = undefined;
        }
      }
    },
    handleInputClick() {
      if (this.$isMobile()) {
        if (this.$refs.dialogDatePicker) {
          this.$refs.dialogDatePicker.show();
        }
      }
    },
    closeMobileDialog() {
      if (this.$refs.dialogDatePicker) {
        this.$refs.dialogDatePicker.hide();
      }
    },
    valueChange(event) {
      this.$nextTick(() => {
        if (
          this.selection === 'single' ||
          (this.selection === 'range' &&
            this.currentValue &&
            this.currentValue.length === 2 &&
            this.currentValue[1] != undefined) ||
          (this.selection === 'range' && !this.currentValue)
        ) {
          this.$emit('value-change', event.value);
          if (this.currentValue) this.$refs.dialogDatePicker.hide();
        }
      });
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control" :class="[className]">
    <AkLabel v-if="label && !inline && displayLabel" :required="!hideRequired && isRequired">
      {{ label }}
    </AkLabel>
    <div class="relative flex items-center">
      <Calendar
        v-if="!$isMobile()"
        v-model="currentValue"
        class="w-full text-grey-900 text-base font-inter form-control"
        :class="[inputClass, {'p-invalid': isInvalid && alreadySubmitted, disabled: isDisabled}]"
        :input-class="{'!pr-12 text-nowrap text-ellipsis': icon}"
        :dateFormat="format"
        :disabled="isDisabled"
        :max-date="maxDate"
        :min-date="minDate"
        :disabled-dates="disabledDates"
        :show-time="showTime"
        :time-only="timeOnly"
        @hide="onHide"
        :base-z-index="this.baseZIndex"
        @dateSelect="valueChange"
        @clear-click="valueChange"
        @today-click="valueChange"
        :view="view"
        :show-button-bar="showButtonBar"
        :placeholder="getPlaceholder"
        :selection-mode="selection"
        :hideOnRangeSelection="hideOnRangeSelection"
        :touchUI="false" />
      <div v-else class="w-full" @click="handleInputClick">
        <input
          type="text"
          readonly
          class="w-full text-grey-900 text-base font-inter form-control p-inputtext p-component"
          :class="[
            inputClass,
            {
              'p-invalid': isInvalid && alreadySubmitted,
              disabled: isDisabled,
              '!pr-12 text-nowrap text-ellipsis': icon,
            },
          ]"
          :placeholder="getPlaceholder"
          :disabled="isDisabled"
          :value="formattedValue" />
      </div>
      <i v-if="icon" :class="icon" class="absolute right-5 text-2xl text-grey-900 pointer-events-none" />
    </div>
    <div class="flex flex-col">
      <small v-if="isRequiredFailed && !isInline" class="p-error">{{ $t('field_required', [label]) }}</small>
      <small v-if="isValidatorMinDate && !isInline" class="p-error">{{ $t('error_min_date', [label]) }}</small>
      <small v-if="isValidatorMaxDate && !isInline" class="p-error">{{ $t('error_max_date', [label]) }}</small>
      <small v-if="isCustomValidatorFailed && !isInline" class="p-error">{{ this.getCustomValidatorMessage }}</small>
    </div>

    <!-- Mobile modal for iOS -->
    <AkDialogHeadless
      ref="dialogDatePicker"
      dismissable
      :show-cancel-button="false"
      :show-validate-button="false"
      content-class="!px-0 !py-2"
      width="100%">
      <div class="flex flex-col h-full w-full justify-center pb-8 p-4">
        <div class="flex justify-between items-center mb-4">
          <span class="font-bold text-lg">{{ label || placeholder || 'Sélectionner une date' }}</span>
          <i class="ga-icon ga-times text-2xl cursor-pointer" @click="closeMobileDialog" />
        </div>
        <Calendar
          v-model="currentValue"
          class="w-full text-grey-900 text-base font-inter"
          :dateFormat="format"
          :disabled="isDisabled"
          :max-date="maxDate"
          :min-date="minDate"
          :disabled-dates="disabledDates"
          :show-time="showTime"
          :time-only="timeOnly"
          :base-z-index="baseZIndex"
          @dateSelect="valueChange"
          @clear-click="valueChange"
          @today-click="valueChange"
          :view="view"
          :show-button-bar="showButtonBar"
          :selection-mode="selection"
          :hideOnRangeSelection="hideOnRangeSelection"
          :inline="true" />
      </div>
    </AkDialogHeadless>
  </div>
</template>
